import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import axiosInstance from 'src/utils/axios';
import { BILLING_NMI_URL } from 'src/config';

const ALLOWED_ORIGINS = new Set([
  'https://billing.1houseglobal.com',
  'https://1house.freshdesk.com',
]);

const isAllowedOrigin = (origin) => {
  if (!origin) return false;
  if (ALLOWED_ORIGINS.has(origin)) return true;
  try {
    const { hostname } = new URL(origin);
    return hostname === 'billing.1houseglobal.com' || hostname.endsWith('.1houseglobal.com');
  } catch (err) {
    return false;
  }
};

const isCloseMessage = (payload) => {
  if (payload === 'Close Popup') return true;
  if (payload && typeof payload === 'object') {
    if (payload.action === 'closeIframe' ) return true;
    if (payload.data && typeof payload.data === 'object') {
      if (payload.action === 'closeIframe') return true;
    }
    return payload.message === 'Close Popup' || payload.type === 'Close Popup';
  }
  return false;
};

const NmiBillingPopup = ({ enabled, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('[NMI Popup] enabled changed', enabled);
    if (!enabled) return;

    let cancelled = false;

    const run = async () => {
      console.log('[NMI Popup] open requested');
      setLoading(true);
      setError('');
      setOpen(true);

      try {
        const ssoResponse = await axiosInstance.get('/api/sso/redirect');
        console.log('[NMI Popup] sso response', ssoResponse?.data);
        const { status, sso_token } = ssoResponse.data || {};
        if (!status || !sso_token) {
          throw new Error('Invalid NMI SSO response');
        }

        const targetUrl = `${BILLING_NMI_URL}/?sso=${encodeURIComponent(sso_token)}`;

        if (!cancelled) {
          setIframeUrl(targetUrl);
          setOpen(true);
        }
      } catch (err) {
        console.error('Failed to load NMI billing popup:', err);
        if (!cancelled) {
          console.log('[NMI Popup] error set, closing');
          setError('Unable to open billing popup.');
          setOpen(false);
          setIframeUrl('');
        }
      } finally {
        if (!cancelled) {
          console.log('[NMI Popup] loading complete');
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => {
      setError('');
      if (onClose) {
        onClose();
      }
    }, 1200);

    return () => clearTimeout(timer);
  }, [error, onClose]);

  const handleClose = useCallback(() => {
    console.log('[NMI Popup] close requested');
    setOpen(false);
    setIframeUrl('');
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!open) return;
      console.log('[NMI Popup] message', {
        origin: event?.origin,
        data: event?.data,
      });
      if (event?.origin && !isAllowedOrigin(event.origin)) return;
      if (isCloseMessage(event?.data)) {
        console.log('Close Popup');
        handleClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleClose, open]);

  if (!enabled && !open && !loading && !error) return null;

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(3px)',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 980,
          height: 'min(90vh, 900px)',
          backgroundColor: '#0f1115',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              color: '#cdd2d8',
            }}
          >
            <CircularProgress color="inherit" />
            <Typography variant="body2">Loading billing portal...</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1,
              color: '#f5b6b6',
              px: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle1">{error}</Typography>
            <Typography variant="body2" sx={{ color: '#cdd2d8' }}>
              Closing the billing popup.
            </Typography>
          </Box>
        )}

        {!loading && !error && open && (
          <Box sx={{ flex: 1 }}>
            <iframe
              title="NMI Billing"
              src={iframeUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              sandbox="allow-same-origin allow-forms allow-scripts allow-popups allow-modals allow-presentation"
              allow="payment"
            />
          </Box>
        )}
      </Box>
    </Box>,
    document.body
  );
};

export default NmiBillingPopup;
